/**
 * App-wide D-pad navigation for TV remotes (the TV-first rule).
 *
 * A remote has arrows, OK and Back — no pointer and no Tab. The guide and Home
 * already move focus with their own roving handlers; everywhere else (the nav
 * rail, the top bar, Movies/Series/Search/Settings, dialogs, the player's
 * controls) nothing did, so on a TV a user was stuck on whatever screen they
 * landed on. This moves focus to the nearest focusable control in the pressed
 * direction, the way TV apps and smart-TV browsers do.
 *
 * Screen handlers keep priority: this acts only on an arrow they left alone
 * (event.defaultPrevented false). They hand over at their edges, so Left from
 * Home's first column or the guide's channel list reaches the nav rail. With
 * nothing in the pressed direction focus stays where it is — the Android
 * WebView would otherwise move it by its own, surprising rules.
 *
 * Kept out of the way of controls that use arrows themselves: a text field
 * keeps Left/Right for its caret and a slider (seek, volume) for its value;
 * Up/Down still leave them. A dialog keeps focus inside itself, and so does
 * the player in full screen.
 */

export type Direction = "up" | "down" | "left" | "right";

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const DIRECTION_BY_KEY: Record<string, Direction> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
};

/**
 * The best candidate from `from` in `direction`, or -1. Pure geometry, so it is
 * testable without a DOM: a candidate must lie in the pressed direction; one
 * lined up with `from` (overlapping it across the axis) beats any that is not,
 * and the nearest wins within each group — so Down in a poster grid goes to
 * the poster below, and Down in the nav rail to the next rail item.
 */
export function pickNext(from: Box, candidates: readonly Box[], direction: Direction, viewportHeight?: number): number {
  const horizontal = direction === "left" || direction === "right";
  let best = -1;
  let bestScore = Infinity;
  candidates.forEach((box, index) => {
    // In the pressed direction: past from's leading edge (a little overlap
    // allowed, for tightly packed rows).
    const SLACK = 4;
    const inDirection =
      direction === "right" ? box.left >= from.right - SLACK && box.right > from.right
      : direction === "left" ? box.right <= from.left + SLACK && box.left < from.left
      : direction === "down" ? box.top >= from.bottom - SLACK && box.bottom > from.bottom
      : box.bottom <= from.top + SLACK && box.top < from.top;
    if (!inDirection) return;
    const gap = Math.max(
      0,
      direction === "right" ? box.left - from.right
      : direction === "left" ? from.left - box.right
      : direction === "down" ? box.top - from.bottom
      : from.top - box.bottom,
    );
    // Across the axis: 0 when the projections overlap, else the distance between them.
    const across = horizontal
      ? Math.max(0, box.top - from.bottom, from.top - box.bottom)
      : Math.max(0, box.left - from.right, from.left - box.right);
    const aligned = across === 0;
    // Up/Down stay within a 45° cone: never further sideways than they move.
    // Otherwise Down from the nav rail's last item jumped into the page. Left
    // and Right keep the wider reach, so Left from any tile finds the rail.
    if (!aligned && !horizontal && across > gap + 8) return;
    // Left/Right reach wide, but only to something ON SCREEN when it is not
    // lined up: Right from a Settings checkbox with nothing beside it went to
    // a button 2,500 px further down the page (health check, 2026-09-27).
    // Lined-up targets off screen — the next tile of a row running off the
    // right — are still reached.
    if (!aligned && horizontal && viewportHeight !== undefined && (box.bottom <= 0 || box.top >= viewportHeight)) return;
    // Anything lined up with `from` wins outright: Down in the nav rail stays in
    // the rail even across its big gap, where a tile off to the side was nearer
    // (seen on a phone with the page scrolled).
    const score = aligned ? gap : 1_000_000 + gap + across * 3;
    if (score < bestScore) {
      bestScore = score;
      best = index;
    }
  });
  return best;
}

/**
 * The nearest of `candidates` that lies ABOVE `from`, measured across the axis
 * instead of up it, or -1.
 *
 * `pickNext` keeps Up and Down inside a 45° cone — never further sideways than
 * they move — because Down from the nav rail's last item otherwise jumped into
 * the page (seen on a phone with the page scrolled). The cost is one case: Up
 * from the rail's own top item finds nothing. The top bar IS above the rail,
 * but the search field overlaps the rail's toggle vertically and sits ~40 px to
 * its right, which the cone reads as "far more sideways than up" (owner,
 * 2026-09-22: "up from the nav open close arrow doesn't go to the search bar
 * when it obviously should").
 *
 * So the same measurement the cone throws away is offered separately, and used
 * for exactly that direction from exactly that region. Down keeps the cone
 * untouched — this is not a general widening.
 */
export function pickAbove(from: Box, candidates: readonly Box[]): number {
  let best = -1;
  let bestScore = Infinity;
  candidates.forEach((box, index) => {
    // Above `from`, allowing the few pixels of overlap two stacked bars have.
    if (box.bottom > from.top + 4) return;
    const across = Math.max(0, box.left - from.right, from.left - box.right);
    if (across < bestScore) {
      bestScore = across;
      best = index;
    }
  });
  return best;
}

/**
 * The nearest of `candidates` below (or above) `from`, with no cone: the
 * straight-line distance between the two boxes' nearest edges decides. Used
 * only inside a popup, where every control belongs to the same small panel.
 */
export function pickAnyway(from: Box, candidates: readonly Box[], direction: "up" | "down"): number {
  let best = -1;
  let bestDistance = Infinity;
  candidates.forEach((box, index) => {
    const beyond = direction === "down" ? box.top >= from.bottom - 4 && box.bottom > from.bottom : box.bottom <= from.top + 4 && box.top < from.top;
    if (!beyond) return;
    const dy = direction === "down" ? Math.max(0, box.top - from.bottom) : Math.max(0, from.top - box.bottom);
    const dx = Math.max(0, box.left - from.right, from.left - box.right);
    const distance = Math.hypot(dx, dy);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}

/**
 * The nearest of `candidates` that lies in `direction` from `from`, for the
 * full-screen player. A target must really lie that way — within a cone, 45°
 * for Left/Right and wider for Up/Down (up to twice as far across as along) —
 * and among those the straight-line distance between the boxes decides. The
 * page rule reaches wide sideways (Left from any tile finds the nav rail),
 * which in full screen sent Left from the control bar's first button up to the
 * settings gear in the far corner, and Up from the top of the recent channels
 * over to it too (projector, 2026-09-26). Up/Down's wider cone lets Up from the
 * control bar reach the recent-channels column on the right — wide enough that
 * the NEAREST item wins wherever the bar sits: at 1.5 the nearest fell just
 * outside when a shorter channel name moved the bar left, and Up from Pause
 * took the fourth item.
 */
export function pickInCone(from: Box, candidates: readonly Box[], direction: Direction): number {
  const horizontal = direction === "left" || direction === "right";
  let best = -1;
  let bestDistance = Infinity;
  candidates.forEach((box, index) => {
    const S = 4;
    const beyond =
      direction === "right" ? box.left >= from.right - S && box.right > from.right
      : direction === "left" ? box.right <= from.left + S && box.left < from.left
      : direction === "down" ? box.top >= from.bottom - S && box.bottom > from.bottom
      : box.bottom <= from.top + S && box.top < from.top;
    if (!beyond) return;
    const along = Math.max(0, direction === "right" ? box.left - from.right : direction === "left" ? from.left - box.right : direction === "down" ? box.top - from.bottom : from.top - box.bottom);
    const across = horizontal
      ? Math.max(0, box.top - from.bottom, from.top - box.bottom)
      : Math.max(0, box.left - from.right, from.left - box.right);
    // Left/Right must be mostly sideways (at most half as far across as along):
    // at 45° Right from the settings gear, top-left, took Close at the bottom of
    // the screen (projector, 2026-09-27). Up/Down reach wider, for the recent-
    // channels column. Across counts one and a half times in the distance, so
    // the column on the SAME side wins: Up from the bar's left end goes to the
    // gear, from its right end to the recent channels.
    if (across > (horizontal ? 0.5 : 2) * along + 8) return;
    // Ties (two buttons both lined up below) go to the one more directly in
    // line, centre to centre: Down from the recent-channels strip took
    // Previous channel, not the ▤ button right under it (2026-09-29).
    const centreOff = horizontal
      ? Math.abs((box.top + box.bottom) / 2 - (from.top + from.bottom) / 2)
      : Math.abs((box.left + box.right) / 2 - (from.left + from.right) / 2);
    const distance = Math.hypot(along, across * 1.5) + centreOff * 0.05;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}

/**
 * The next control inside a popup: rows first. Up/Down go to the NEAREST row
 * that way, then to the control in it closest across; Left/Right stay in the
 * row (lined up with `from`), reaching out only when the row has nothing that
 * way. The page rule lets anything lined up win outright however far, and in
 * the player's settings Down from Close took the speed row at the bottom — the
 * widest row, so the one "lined up" — skipping picture size and subtitles, and
 * Up from the speeds went straight back to Close (projector, 2026-09-26).
 */
export function pickInPopup(from: Box, candidates: readonly Box[], direction: Direction): number {
  const S = 4;
  const beyond = (box: Box) =>
    direction === "right" ? box.left >= from.right - S && box.right > from.right
    : direction === "left" ? box.right <= from.left + S && box.left < from.left
    : direction === "down" ? box.top >= from.bottom - S && box.bottom > from.bottom
    : box.bottom <= from.top + S && box.top < from.top;
  const centre = (box: Box) => ({ x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 });
  const f = centre(from);
  if (direction === "up" || direction === "down") {
    const gapOf = (box: Box) => Math.max(0, direction === "down" ? box.top - from.bottom : from.top - box.bottom);
    let nearestRow = Infinity;
    candidates.forEach((box) => {
      if (beyond(box)) nearestRow = Math.min(nearestRow, gapOf(box));
    });
    if (nearestRow === Infinity) return -1;
    let best = -1;
    let bestAcross = Infinity;
    candidates.forEach((box, index) => {
      if (!beyond(box) || gapOf(box) > nearestRow + 8) return;
      const across = Math.abs(centre(box).x - f.x);
      if (across < bestAcross) {
        bestAcross = across;
        best = index;
      }
    });
    return best;
  }
  // Left/Right: the nearest in the same row; else the nearest within a 45° cone.
  let best = -1;
  let bestGap = Infinity;
  candidates.forEach((box, index) => {
    if (!beyond(box)) return;
    const sameRow = box.top < from.bottom - S && box.bottom > from.top + S;
    if (!sameRow) return;
    const gap = Math.abs(centre(box).x - f.x);
    if (gap < bestGap) {
      bestGap = gap;
      best = index;
    }
  });
  return best >= 0 ? best : pickInCone(from, candidates, direction);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isShown(element: HTMLElement): boolean {
  if (element.closest("[hidden], [inert], [aria-hidden='true']")) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return false;
  const style = getComputedStyle(element);
  return style.visibility !== "hidden" && style.display !== "none" && Number(style.opacity) > 0.05;
}

/**
 * An open popup: a dialog or a menu. A popup keeps focus inside itself until it
 * closes (owner, 2026-09-26). Menus were missing: Up in the guide's long-press
 * menu picked the channel row hidden under it, and focus left the open menu.
 * Alert dialogs were missing too: Up or Down on "Are you sure you want to
 * leave?" took focus out of it before Stay or Leave was pressed (onn box,
 * owner, 2026-10-05). The upgrade prompt is one as well.
 */
const POPUP = '[role="dialog"], [role="alertdialog"], [role="menu"]';

/** Where focus may go right now: an open popup, the full-screen player, or the whole app. */
function scope(active: Element | null): ParentNode {
  const dialog =
    active?.closest(POPUP) ??
    document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"], [role="menu"]');
  if (dialog) return dialog;
  if (document.documentElement.classList.contains("player-fullscreen")) {
    const player = document.querySelector(".player-surface--fullscreen");
    if (player) return player;
  }
  return document;
}

/**
 * Arrows a control uses itself: caret keys in a text field, value keys on a
 * slider. A text field keeps Left only while its caret can still move left,
 * and Right while it can still move right. At the end, the arrow leaves the
 * field for the closest control, as it does on a TV: kept for good, the
 * WebView moved focus by its own rules instead — Left from Home's empty
 * search box landed on the nav rail's container, which is not a control
 * (projector, 2026-09-26).
 */
export function keepsArrow(target: HTMLElement, direction: Direction): boolean {
  if (direction === "up" || direction === "down") return false;
  if (target.isContentEditable || target.tagName === "TEXTAREA") return true;
  if (target.tagName !== "INPUT") return false;
  const input = target as HTMLInputElement;
  const type = input.type;
  if (type === "range") return true;
  if (!(type === "text" || type === "search" || type === "email" || type === "url" || type === "password" || type === "number" || type === "tel")) return false;
  let start: number | null = null;
  let end: number | null = null;
  try {
    start = input.selectionStart;
    end = input.selectionEnd;
  } catch {
    // email and number fields do not report a caret: keep their arrows.
  }
  if (start === null || end === null) return true;
  return direction === "left" ? !(start === 0 && end === 0) : !(start === input.value.length && end === input.value.length);
}

function toBox(rect: DOMRect): Box {
  return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
}

/**
 * The right edge of the nav rail when it FLOATS over the page, else null.
 *
 * In the guide's Overlay layout the rail is position: fixed over the full-width
 * guide, so the channel list starts underneath it (x 12 against a rail that
 * ends at 53, on the projector). Measured by their full boxes, the list is not
 * to the rail's right and the rail is not to the list's left: Right from the
 * rail skipped the guide and landed on the channel-number box in the far
 * corner, and nothing led back (owner, 2026-09-24: "the layouts for epg don't
 * behave the same"). A viewer sees only the part the rail leaves uncovered, so
 * that is the part we measure.
 */
function floatingRailEdge(): number | null {
  const rail = document.querySelector<HTMLElement>(".navrail");
  if (!rail || getComputedStyle(rail).position !== "fixed") return null;
  return rail.getBoundingClientRect().right;
}

/** `el`'s box, minus whatever of it a floating rail covers. */
function visibleBox(el: HTMLElement, railEdge: number | null): Box {
  const box = toBox(el.getBoundingClientRect());
  if (railEdge === null || box.right <= railEdge || el.closest(".navrail")) return box;
  return { ...box, left: Math.max(box.left, railEdge) };
}

/** Move focus one step in `direction`. Returns false when there was nowhere to go. */
export function moveFocus(direction: Direction): boolean {
  const active = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
  const root = scope(active);
  const candidates = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el !== active && isShown(el));
  if (candidates.length === 0) return false;
  // Focus has slipped out of an open popup: any arrow brings it back in.
  if (root !== document && root instanceof HTMLElement && root.matches(POPUP) && active && !root.contains(active)) {
    candidates[0]!.focus();
    return true;
  }
  // The nav rail is the app's left edge: nothing lies left of it for a viewer.
  // Measured by position alone, a row scrolled sideways put tiles off screen
  // at negative x, and Left from the rail jumped into the page to one of them
  // (projector, 2026-09-25).
  if (direction === "left" && active?.closest(".navrail")) return false;
  // The top bar is the app's top edge, the same way. Page controls scrolled
  // above the screen are above it by position, and Up from the search box
  // went back DOWN into Settings to one of them (projector, 2026-09-29).
  if (direction === "up" && active?.closest(".topbar")) return false;
  let next: HTMLElement | undefined;
  if (!active) {
    // Nothing focused yet (a fresh screen): start at the control a screen names
    // as its starting point (data-nav-start: the full-screen player's
    // Play/Pause, where the first Left/Right used to land on the gear in the
    // corner — projector, 2026-09-26); else at the top-left of the content.
    next = candidates.find((el) => el.hasAttribute("data-nav-start")) ?? [...candidates].sort((a, b) => {
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      return ra.top - rb.top || ra.left - rb.left;
    })[0];
  } else {
    const railEdge = floatingRailEdge();
    const from = visibleBox(active, railEdge);
    // Up and Down in the nav rail stay in the rail: it is one column. Once the
    // rail scrolled, Down from Me (its last item) found a Home tile below the
    // screen, and the page jumped to it (projector, 2026-09-26).
    const inRail = active.closest(".navrail") && (direction === "up" || direction === "down");
    // From the page, Left/Right never land in the top bar: it is reached with
    // Up. The page rule reaches wide sideways, and Right from a Settings
    // checkbox with nothing beside it jumped to the voice-search button in
    // the top-right corner (health check, 2026-09-27). From the rail and
    // inside the bar itself, nothing changes.
    const sidewaysFromPage =
      (direction === "left" || direction === "right") && !active.closest(".navrail, .topbar");
    // And the other way round: Left/Right inside the top bar stay in the bar (or reach the rail,
    // which lies to its left), never down into the page. Right from the bell, the bar's last
    // control, jumped to an Apps tile off the right of the screen (onn box, launcher mode,
    // 2026-10-20); with nothing to the right it now stays put.
    const sidewaysInBar = (direction === "left" || direction === "right") && Boolean(active.closest(".topbar"));
    const pool = inRail
      ? candidates.filter((el) => el.closest(".navrail"))
      : sidewaysFromPage
        ? candidates.filter((el) => !el.closest(".topbar"))
        : sidewaysInBar
          ? candidates.filter((el) => el.closest(".topbar, .navrail"))
          : candidates;
    const inPlayer = root instanceof HTMLElement && root.matches(".player-surface--fullscreen");
    const inPopup = root instanceof HTMLElement && root.matches(POPUP);
    const pick = (els: HTMLElement[]): HTMLElement | undefined => {
      const boxes = els.map((el) => visibleBox(el, railEdge));
      const index = inPopup ? pickInPopup(from, boxes, direction) : inPlayer ? pickInCone(from, boxes, direction) : pickNext(from, boxes, direction, window.innerHeight);
      return index >= 0 ? els[index] : undefined;
    };
    // Up/Down from the page stay in the page while it has anything that way,
    // scrolled off screen or not; the top bar is for Up from the top. In
    // Settings, the control above was scrolled out of sight and the fixed top
    // bar measured nearer, so every other Up landed on the search box
    // (projector, 2026-09-29).
    //
    // And not into the nav rail before the top bar: the rail is to the LEFT.
    // Up from Home's ▶ Play Now, with nothing above it in the page, went
    // diagonally down-left to Series in the rail, while the search box sat
    // straight above it (owner, 2026-10-06: Up goes straight up). Left still
    // reaches the rail from anywhere.
    const verticalFromPage = root === document && (direction === "up" || direction === "down") && !active.closest(".navrail, .topbar");
    next = verticalFromPage
      ? (pick(pool.filter((el) => !el.closest(".topbar, .navrail"))) ?? pick(pool.filter((el) => !el.closest(".navrail"))) ?? pick(pool))
      : pick(pool);
    // Up from the rail's top item: the rail has nothing above it, and the top
    // bar — the thing that IS above it — is the one case the cone rejects. See
    // pickAbove. Only this direction, only from the rail.
    // Inside a popup, Up/Down reach the nearest control that way even outside
    // the cone: the programme card's Close sits under Watch on the left, and
    // Down from Record, Track or Favorite found nothing (projector, 2026-09-26).
    // The cone exists for the nav rail, which a popup never contains.
    if (!next && (direction === "up" || direction === "down") && root instanceof HTMLElement && root.matches(POPUP)) {
      const way = pickAnyway(from, candidates.map((el) => visibleBox(el, railEdge)), direction);
      if (way >= 0) next = candidates[way];
    }
    if (!next && direction === "up" && active.closest(".navrail")) {
      // The guide's toolbar plays the top bar's part on Live TV, where Up from
      // the rail's top button otherwise went nowhere (projector, 2026-09-26).
      const bar = candidates.filter((el) => el.closest(".topbar, .epg-toolbar"));
      const up = pickAbove(from, bar.map((el) => toBox(el.getBoundingClientRect())));
      if (up >= 0) next = bar[up];
    }
  }
  if (!next) return false;
  next.focus();
  next.scrollIntoView({ block: "nearest", inline: "nearest" });
  return true;
}

/**
 * Install on the window. Screen handlers are also window listeners, some added
 * after this one, so this must run LAST to see whether they used the key: the
 * capture-phase listener adds a one-off bubble-phase listener, which the DOM
 * runs after every listener already on the window.
 */
export function installSpatialNavigation(): () => void {
  const decide = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    const direction = DIRECTION_BY_KEY[event.key];
    if (!direction) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target && keepsArrow(target, direction)) return;
    if (moveFocus(direction)) {
      event.preventDefault();
      return;
    }
    // Nothing that way: focus stays put, as in a TV app. Left alone, the
    // Android WebView moves focus by its own rules — on a phone, Down from the
    // nav rail's last item jumped back UP into the page.
    if (target && target !== document.body) event.preventDefault();
  };
  const capture = (event: KeyboardEvent) => {
    if (!DIRECTION_BY_KEY[event.key]) return;
    window.addEventListener("keydown", decide, { once: true });
  };
  window.addEventListener("keydown", capture, { capture: true });
  return () => {
    window.removeEventListener("keydown", capture, { capture: true });
    window.removeEventListener("keydown", decide);
  };
}
